/* eslint-disable @next/next/no-img-element */
import {
  basicPageProps,
  fetchTemplate,
  isServer,
  mobileProps,
} from '@/utils/isServer';
import Title from '@components2/Landing/Title';
import { absoluteServerPath, resolveConfigUrl } from '@helpers/api';
import { cacheFetch } from '@helpers/cacheFetch';
import { Collapse } from 'antd';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRef, useState } from 'react';

const SectionItem = dynamic(() => import('@components2/Landing/SectionItem'), {
  ssr: true,
});

const Airports = dynamic(() => import('@components/Airports'), {
  ssr: true,
});

const Guides = dynamic(() => import('@components/Guides'), {
  ssr: true,
});

const Monthlies = dynamic(() => import('@components/Monthlies'), {
  ssr: true,
});

const { Panel } = Collapse;

function extractPrefixSuffix(url) {
  let supressFromHome = false;
  let suffix = '';
  let prefix = '/guide/';
  if (url.indexOf('?') != -1) {
    suffix = url.substring(url.indexOf('?'));
  }
  if (url.indexOf('/myapp') != -1) {
    prefix = '/myapp/guide/';
  }
  if (suffix == '?fromhome=true') {
    suffix = '';
    supressFromHome = true;
  }
  return [prefix, suffix, supressFromHome];
}

// // This also gets called at build time
export const getServerSideProps: GetServerSideProps = async ({
  req,
  params,
  query,
}) => {
  const { isMobile, isIos } = mobileProps({ req });
  const { configInfo, airports, baseUrl } = await basicPageProps({ req });
  const { t } = query; // Preview

  let landing;
  const friendlyUrl = params.friendlyUrl as string;
  try {
    if (t === 'preview') {
      const landingResponse = await fetch(
        `${baseUrl}/api/AirportLandingGuide/${friendlyUrl}?t=preview`,
        { ...fetchTemplate }
      );
      landing = await landingResponse.json();
    } else {
      landing = await cacheFetch(
        `${baseUrl}/api/AirportLandingGuide/${friendlyUrl}`
      );
    }
    if (
      t !== 'preview' &&
      landing.guide_friendly_url &&
      landing.guide_friendly_url != friendlyUrl
    ) {
      const [prefix, suffix] = extractPrefixSuffix(req.url);
      return {
        redirect: {
          destination: prefix + landing.guide_friendly_url + suffix,
          permanent: true,
        },
      } as any;
    }
  } catch (e) {
    return {
      redirect: {
        destination: '/',
      },
    } as any;
  }
  const [prefix, suffix, supressFromHome] = extractPrefixSuffix(req.url);
  if (supressFromHome) {
    return {
      redirect: {
        destination: prefix + landing.airport_frienldy_url + suffix,
        permanent: true,
      },
    } as any;
  }

  return {
    props: {
      airports,
      landing,
      configInfo,
      deviceType: isMobile ? 'mobile' : 'desktop',
    },
  };
};

// pages/parking/[friendlyUrl].js
function Guide({ airports, landing, configInfo, deviceType }) {
  const landingAirportInitials = landing?.properties?.airport_initials;
  const pageTitle = landing?.properties?.seo_title
    ? landing?.properties?.seo_title
    : `${landing?.properties?.airport_name ?? ''} (${
        landingAirportInitials ?? ''
      }) | On Air Parking Cheap Discount Rates`;
  const metaTitle = pageTitle;
  const metaStaticBlurb = `Cheap ${
    landing?.properties?.seo_title ?? ''
  } Parking At ${
    landing?.properties?.display_name ?? ''
  }. We know, the price looks to good to be true! Don't worry, it's true, and it's a great location. In fact you'll be parking at a professional parking facility. You'll be e-mailed where you're parking for $2.75 a day once you make the purchase on our site. Reserve today!`;
  const metaDescription = landing?.properties?.landing_seo_description
    ? landing?.properties?.landing_seo_description
    : metaStaticBlurb;
  const metaKeywords = landing?.landing_seo_keywords ?? '';
  const refLanding = useRef(null);
  const [showBar, setShowBar] = useState(true);
  return (
    <div className="relative">
      <Head>
        <title>{pageTitle}</title>
        <meta property="og:title" content={metaTitle} key="og:title" />
        <meta
          property="og:description"
          content={metaDescription}
          key="og:description"
        />
        <meta name="description" content={metaDescription} key="description" />
        {metaKeywords && (
          <meta name="keywords" content={metaKeywords} key="keywords" />
        )}
        {landing?.metainfo?.map((meta) => meta.content)}
        {landing?.properties?.landing_display_header}
        {landing?.schemas?.map((schema, index) => (
          <script
            type="application/ld+json"
            key={index}
            dangerouslySetInnerHTML={{
              __html: schema.schema_object,
            }}
          ></script>
        ))}
      </Head>
      <div
        className="landing high-content container max-w-4xl mx-auto px-4 h-screen overflow-hidden relative"
        ref={refLanding}
      >
        <article>
          <h2 className="text-2xl text-center px-4">{landing?.page_title}</h2>
          <div className="high-content flex flex-col gap-2">
            {landing?.sections
              ?.sort((a, b) => {
                return a.order - b.order;
              })
              .map((section, index) =>
                section.type !== 'Accordion' ? (
                  <section key={index}>
                    <header>
                      {section?.tag && <Title value={section?.tag} />}
                    </header>
                    <SectionItem
                      section={section}
                      configInfo={configInfo}
                      fallbackImageAlt={landing?.page_title}
                    />
                  </section>
                ) : (
                  <Collapse collapsible="header" key={index}>
                    <Panel header={section?.tag} key={index}>
                      <SectionItem
                        section={section}
                        configInfo={configInfo}
                        fallbackImageAlt={landing?.page_title}
                      />
                    </Panel>
                  </Collapse>
                )
              )}
          </div>
        </article>
        {showBar && (
          <div
            id="content-bar"
            className="absolute w-full left-0 flex items-center justify-center bottom-0 h-12 cursor-pointer"
            onClick={() => {
              setShowBar(false);
              refLanding.current.classList.remove('h-screen');
              refLanding.current.classList.remove('overflow-hidden');
            }}
          >
            <div className="mx-auto flex itens-center justify-center font-medium text-lg">
              Click to read more
            </div>
          </div>
        )}
      </div>

      <Airports airports={airports} />
      <Guides airports={airports} />
      <Monthlies airports={airports} />
    </div>
  );
}

export default observer(Guide);
